import { SeoHead } from '@/components/SeoHead';
import { LocalBusinessStructuredData } from '@/components/StructuredData';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { ContactForm } from '@/components/ContactForm';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { MapPin, Mail, Instagram, MessageCircle, CreditCard, Cloud, Shield, Check, AlertCircle, FileText, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Contact() {
  return <>
      <SeoHead
        title="Contact & Réservation - Roca Rio | Réservez votre excursion à Rio"
        description="Réservez votre excursion à Rio avec Cléa, guide francophone certifiée. Contact direct par WhatsApp, email ou formulaire. Réponse garantie en 24h."
        canonical="https://rocario-cleacarioca.com/contact"
        keywords="réservation excursion Rio, contact guide francophone Rio, booking Rio de Janeiro, réserver visite Rio, WhatsApp Roca Rio"
      />
      
      <LocalBusinessStructuredData type="TouristInformationCenter" />

      <Header />
      
      <main className="min-h-screen bg-background">
        {/* Hero éditorial */}
        <section className="py-20 md:py-28">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <div className="w-12 h-1 bg-roca-yellow mb-6" />
              <h1 className="text-3xl md:text-5xl font-playfair font-bold mb-4">
                Réservez votre excursion
              </h1>
              <p className="text-base md:text-xl text-muted-foreground max-w-2xl">
                Remplissez ce formulaire et recevez une proposition personnalisée par WhatsApp
              </p>
            </div>
          </div>
        </section>

        {/* Formulaire */}
        <section className="pb-16 md:pb-20 -mt-8">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <Card className="shadow-2xl border-2 border-roca-yellow/20">
                <CardContent className="p-4 md:p-8">
                  <div className="mb-4 md:mb-6 flex items-start gap-3 p-3 md:p-4 bg-roca-orange/5 rounded-lg border border-roca-orange/20">
                    <MessageCircle className="w-5 h-5 text-roca-orange mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <p className="font-semibold text-foreground mb-1">Comment ça marche ?</p>
                      <p className="text-muted-foreground">
                        Une fois le formulaire envoyé, vous recevrez automatiquement un message WhatsApp 
                        pré-rempli avec toutes vos informations. Il vous suffira de l'envoyer pour finaliser votre réservation !
                      </p>
                    </div>
                  </div>
                  
                  <ContactForm />
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Informations pratiques */}
        <section className="py-16 md:py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center">
                Informations pratiques
              </h2>
              
              <Accordion type="multiple" className="space-y-3">
                <AccordionItem value="payment" className="border rounded-lg bg-background">
                  <AccordionTrigger className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-5 h-5 text-roca-orange" />
                      <span className="font-medium">Paiement & Confirmation</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-3">
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2"><Check className="w-4 h-4 text-roca-orange mt-0.5 flex-shrink-0" /><p>Réponse sous 24h par WhatsApp ou email</p></div>
                      <div className="flex items-start gap-2"><Check className="w-4 h-4 text-roca-orange mt-0.5 flex-shrink-0" /><p>Espèces recommandées (carte bancaire acceptée avec frais de +5%)</p></div>
                      <div className="flex items-start gap-2"><Check className="w-4 h-4 text-roca-orange mt-0.5 flex-shrink-0" /><p>Paiement sur place le jour de l'excursion</p></div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="weather" className="border rounded-lg bg-background">
                  <AccordionTrigger className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Cloud className="w-5 h-5 text-roca-orange" />
                      <span className="font-medium">Météo & Annulation</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-3">
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2"><AlertCircle className="w-4 h-4 text-roca-orange mt-0.5 flex-shrink-0" /><p>Annulation ou report possible en cas de conditions météorologiques défavorables</p></div>
                      <div className="flex items-start gap-2"><AlertCircle className="w-4 h-4 text-roca-orange mt-0.5 flex-shrink-0" /><p>Vous serez prévenu au moins 12h à l'avance</p></div>
                      <div className="flex items-start gap-2"><AlertCircle className="w-4 h-4 text-roca-orange mt-0.5 flex-shrink-0" /><p>Option de report ou remboursement intégral selon votre choix</p></div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="included" className="border rounded-lg bg-background">
                  <AccordionTrigger className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-roca-yellow" />
                      <span className="font-medium">Ce qui est inclus</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-3">
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2"><Check className="w-4 h-4 text-roca-yellow mt-0.5 flex-shrink-0" /><p>Guide francophone certifiée</p></div>
                      <div className="flex items-start gap-2"><Check className="w-4 h-4 text-roca-yellow mt-0.5 flex-shrink-0" /><p>Entrées aux sites (sauf mention contraire)</p></div>
                      <div className="flex items-start gap-2"><Check className="w-4 h-4 text-roca-yellow mt-0.5 flex-shrink-0" /><p>Assurance responsabilité civile</p></div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="safety" className="border rounded-lg bg-background">
                  <AccordionTrigger className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-roca-red" />
                      <span className="font-medium">Sécurité & santé</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-3">
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2"><Shield className="w-4 h-4 text-roca-red mt-0.5 flex-shrink-0" /><p>Guide formée aux premiers secours</p></div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="legal" className="border rounded-lg bg-background">
                  <AccordionTrigger className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-muted-foreground" />
                      <span className="font-medium">Informations légales</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-3">
                    <div className="space-y-3 text-sm">
                      <p>Roca Rio est une agence de tourisme enregistrée au Brésil.</p>
                      <div className="flex gap-3">
                        <Button variant="outline" size="sm" asChild>
                          <Link to="/mentions-legales">Mentions légales</Link>
                        </Button>
                        <Button variant="outline" size="sm" asChild>
                          <Link to="/confidentialite">Confidentialité</Link>
                        </Button>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </section>

        {/* Coordonnées */}
        <section className="py-8">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h3 className="text-lg font-semibold mb-4 text-center">
                Autres moyens de nous joindre
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-roca-orange/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-roca-orange" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium mb-1">Email</p>
                      <a href="mailto:contact.rocario@gmail.com" className="text-sm text-roca-orange hover:underline truncate block">
                        contact.rocario@gmail.com
                      </a>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-roca-red/10 flex items-center justify-center flex-shrink-0">
                      <Instagram className="w-6 h-6 text-roca-red" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium mb-1">Instagram</p>
                      <div className="text-sm space-y-0.5">
                        <a href="https://instagram.com/roca.rio_" target="_blank" rel="noopener noreferrer" className="text-roca-red hover:underline block truncate">
                          @roca.rio_
                        </a>
                        <a href="https://instagram.com/meninos.dobrasil" target="_blank" rel="noopener noreferrer" className="text-roca-red hover:underline block truncate">
                          @meninos.dobrasil
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>;
}
